import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import isEqual from 'lodash/isEqual';
import qs from 'qs';
import { makeStyles } from '@mui/styles';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import capitalize from 'lodash/capitalize';
import groupBy from 'lodash/groupBy';
import mapValues from 'lodash/mapValues';

import { useGroupedAutocompleteStyles } from 'shared/styles/groupedAutocompleteStyles/groupedAutocompleteStyles';
import { InputAdornment, Button } from '@mui/material';

import { searchCategories } from 'shared/constants/searchCategories';
import { useQuery } from 'shared/hooks/useQuery/useQuery';
import { setSearchFiltersAction, searchStartAction } from 'store/searchResult/searchResultActions';
import { performInNextCycle } from 'shared/functions';
import useDeepCompareEffect from 'use-deep-compare-effect';

export const useStyles = makeStyles((theme) => ({
  root: {
    padding: `9px 14px !important`,
  },
  button: {
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    '&:not(:last-child)': {
      marginRight: theme.spacing(1),
    },
  },
}));

const Search = ({ options, getOptionLabel, groupBy: group }) => {
  const groupedAutocompleteClasses = useGroupedAutocompleteStyles();
  const classes = useStyles();

  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const urlParams = useQuery();
  const dispatch = useDispatch();

  const lastVisitedRatingType = useSelector(({ shared }) => shared.lastVisited.ratingType);

  const {
    query,
    region_code: regionCode,
    city,
    postal_code: postalCode,
    country,
    industry,
    ratingType = lastVisitedRatingType,
    module,
  } = urlParams;

  const [inputValue, setInputValue] = useState('');

  const getInitialSearchState = () =>
    options.length
      ? Object.keys({ query, country, industry }).reduce((results, category) => {
          if (!category || !urlParams[category]) return results;

          const values = urlParams[category];

          if (category === searchCategories.QUERY) {
            return [...results, { name: values, value: values, category }];
          }

          return [
            ...results,
            ...values.reduce((all, item) => {
              const fullValue = options.find(({ value }) => value === item);

              if (!fullValue) {
                return all;
              }

              return [
                ...all,
                {
                  name: fullValue.name,
                  value: fullValue.value,
                  category,
                },
              ];
            }, []),
          ];
        }, [])
      : [];

  const [values, setValues] = useState(getInitialSearchState());

  const [shouldSubmit, setShouldSubmit] = useState(false);

  const handleSubmit = () => {
    if (!values.length) return;

    const queryParams = mapValues(groupBy(values, 'category'), (item, key) => {
      const mappedValues = item.map(({ value }) => value);

      if (key === searchCategories.QUERY) return mappedValues.join(' ');
      return mappedValues;
    });

    const queryString = qs.stringify(
      { ...queryParams, regionCode, city, postalCode, ratingType, module },
      { addQueryPrefix: true },
    );

    if (location.search !== queryString) {
      dispatch(searchStartAction);
      dispatch(setSearchFiltersAction({ ...queryParams, ratingType }));

      /* Peform navigate in next event loop cycle of JS engine.
       * Otherwise the browser is occupied with context switching,
       * hence actions dispatching and processing is significantly delayed. */
      performInNextCycle(() => navigate({ pathname: '/search-result', search: queryString }));
    }
  };

  useDeepCompareEffect(() => {
    const initialValues = getInitialSearchState();
    if (!isEqual(initialValues, values)) {
      setValues(getInitialSearchState());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, urlParams]);

  useEffect(() => {
    if (!shouldSubmit) return;

    handleSubmit();

    setShouldSubmit(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldSubmit]);

  const handleChange = (_event, items) => {
    const newValues = items.map((item) =>
      typeof item === 'string'
        ? { category: searchCategories.QUERY, name: item, value: item }
        : item,
    );

    const { length } = newValues;

    if (length && newValues[length - 1].category === searchCategories.QUERY) {
      setShouldSubmit(true);
    }

    setValues(newValues);
  };

  const handleKeyDown = ({ key }) => {
    if (key === 'Enter' && !inputValue.length && values.length) {
      setShouldSubmit(true);
    }
  };

  const optionLabel = ({ name, category }) =>
    category !== 'query' ? `${capitalize(category)}: ${name}` : name;

  const resetValue = () => setValues([]);

  const isFilled = !!values.length;

  return (
    <Autocomplete
      freeSolo
      multiple
      options={options}
      onChange={handleChange}
      getOptionLabel={getOptionLabel}
      groupBy={group}
      value={values}
      inputValue={inputValue}
      onInputChange={(_event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      open={inputValue.length > 0}
      renderInput={(params) => (
        <TextField
          onKeyDown={handleKeyDown}
          {...params}
          InputProps={{
            ...params.InputProps,
            classes: { root: classes.root },
            startAdornment: (
              <>
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
                {params.InputProps.startAdornment}
              </>
            ),
            endAdornment: (
              <InputAdornment position="end">
                {isFilled && (
                  <>
                    <CloseIcon onClick={resetValue} className={classes.button} />
                    <Button
                      className={classes.button}
                      variant="contained"
                      color="primary"
                      onClick={handleSubmit}
                    >
                      {t('Search')}
                    </Button>
                  </>
                )}
              </InputAdornment>
            ),
          }}
          placeholder={t('SEARCH.SEARCH_FOR_ENTITIES_COUNTRIES_AND_INDUSTRIES')}
          variant="outlined"
        />
      )}
      renderTags={(items, getTagProps) =>
        items.map((option, index) => (
          <Chip label={optionLabel(option)} {...getTagProps({ index })} />
        ))
      }
      classes={groupedAutocompleteClasses}
    />
  );
};

Search.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
    }),
  ).isRequired,
  getOptionLabel: PropTypes.func.isRequired,
  groupBy: PropTypes.func.isRequired,
};

export default Search;
